import type {
  AdminOrdersListParams,
  OrderStatus,
  PaymentMethod,
  PaymentProvider,
  PaymentStatus,
  SortDirection,
} from "@/services/admin/orders.types";

export const DEFAULT_ORDERS_PAGE_SIZE = 25;
export const DEFAULT_ORDERS_SORT = "createdAt:desc" as const;

const ORDER_STATUSES: OrderStatus[] = [
  "Draft",
  "PendingPayment",
  "Paid",
  "Cancelled",
  "Failed",
];

const PAYMENT_STATUSES: PaymentStatus[] = [
  "Created",
  "Pending",
  "Authorized",
  "Captured",
  "Failed",
  "Cancelled",
  "Refunded",
];

const PAYMENT_PROVIDERS: PaymentProvider[] = [
  "Unknown",
  "Internal",
  "Stripe",
  "Adyen",
  "Paypal",
  "MercadoPago",
];

const PAYMENT_METHODS: PaymentMethod[] = [
  "Unknown",
  "None",
  "Card",
  "BankTransfer",
  "Pix",
  "PayPal",
];

const SORT_FIELDS = ["createdAt", "updatedAt", "total"] as const;

function parseEnum<T extends string>(value: string | null, allowed: readonly T[]): T | undefined {
  if (!value) return undefined;
  return allowed.includes(value as T) ? (value as T) : undefined;
}

function parsePositiveInt(value: string | null, fallback: number): number {
  if (!value) return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export function parseOrdersSearchParams(searchParams: URLSearchParams): AdminOrdersListParams {
  const sortRaw = searchParams.get("sort") ?? DEFAULT_ORDERS_SORT;
  const [sortField, sortDirectionRaw] = sortRaw.split(":");
  const sortDirection: SortDirection = sortDirectionRaw === "asc" ? "asc" : "desc";
  const sort =
    SORT_FIELDS.includes(sortField as (typeof SORT_FIELDS)[number])
      ? (`${sortField}:${sortDirection}` as AdminOrdersListParams["sort"])
      : DEFAULT_ORDERS_SORT;

  return {
    page: parsePositiveInt(searchParams.get("page"), 1),
    pageSize: parsePositiveInt(searchParams.get("pageSize"), DEFAULT_ORDERS_PAGE_SIZE),
    search: searchParams.get("search") ?? undefined,
    orderStatus: parseEnum(searchParams.get("orderStatus"), ORDER_STATUSES),
    paymentStatus: parseEnum(searchParams.get("paymentStatus"), PAYMENT_STATUSES),
    provider: parseEnum(searchParams.get("provider"), PAYMENT_PROVIDERS),
    method: parseEnum(searchParams.get("method"), PAYMENT_METHODS),
    packageId: searchParams.get("packageId") ?? undefined,
    from: searchParams.get("from") ?? undefined,
    to: searchParams.get("to") ?? undefined,
    sort,
  };
}

export function serializeOrdersSearchParams(params: AdminOrdersListParams): URLSearchParams {
  const next = new URLSearchParams();

  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? DEFAULT_ORDERS_PAGE_SIZE;
  const sort = params.sort ?? DEFAULT_ORDERS_SORT;

  if (page !== 1) next.set("page", String(page));
  if (pageSize !== DEFAULT_ORDERS_PAGE_SIZE) next.set("pageSize", String(pageSize));
  if (params.search?.trim()) next.set("search", params.search.trim());
  if (params.orderStatus) next.set("orderStatus", params.orderStatus);
  if (params.paymentStatus) next.set("paymentStatus", params.paymentStatus);
  if (params.provider) next.set("provider", params.provider);
  if (params.method) next.set("method", params.method);
  if (params.packageId?.trim()) next.set("packageId", params.packageId.trim());
  if (params.from) next.set("from", params.from);
  if (params.to) next.set("to", params.to);
  if (sort !== DEFAULT_ORDERS_SORT) next.set("sort", sort);

  return next;
}

export function hasActiveOrderFilters(params: AdminOrdersListParams): boolean {
  return Boolean(
    params.search?.trim() ||
      params.orderStatus ||
      params.paymentStatus ||
      params.provider ||
      params.method ||
      params.packageId?.trim() ||
      params.from ||
      params.to ||
      (params.sort && params.sort !== DEFAULT_ORDERS_SORT)
  );
}

export function clearOrderFilters(
  params: AdminOrdersListParams
): AdminOrdersListParams {
  return {
    page: 1,
    pageSize: params.pageSize ?? DEFAULT_ORDERS_PAGE_SIZE,
    sort: DEFAULT_ORDERS_SORT,
  };
}

/** Convert date-only input (YYYY-MM-DD) to inclusive UTC start. */
export function dateInputToFromUtc(dateValue: string): string | undefined {
  if (!dateValue) return undefined;
  return `${dateValue}T00:00:00.000Z`;
}

/** Convert date-only input to exclusive UTC end (next day 00:00). */
export function dateInputToToUtc(dateValue: string): string | undefined {
  if (!dateValue) return undefined;
  const date = new Date(`${dateValue}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString();
}

/** Extract YYYY-MM-DD from ISO UTC for date inputs. */
export function utcIsoToDateInput(iso?: string): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}

/** Extract YYYY-MM-DD for exclusive `to` boundary (shows previous inclusive day). */
export function utcExclusiveToToDateInput(iso?: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

export {
  ORDER_STATUSES,
  PAYMENT_STATUSES,
  PAYMENT_PROVIDERS,
  PAYMENT_METHODS,
  SORT_FIELDS,
};
